import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const ROLES = { super_admin: "Super Admin", admin: "Admin", manager: "Manager", sales_executive: "Sales Executive", content_manager: "Content Manager" };
const RANK = { sales_executive: 1, content_manager: 1, manager: 2, admin: 3, super_admin: 4 };
const EMPTY = { name: "", email: "", mobile: "", role: "sales_executive", department: "", status: "active", joining_date: "", password: "" };

export default function Employees() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const canManage = ["super_admin", "admin"].includes(user?.role);

  const load = useCallback(() => api.get("/admin/employees").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const open = (emp) => { setEditing(emp || {}); setForm(emp ? { ...emp, password: "" } : EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const res = editing?.id
        ? await api.put(`/admin/employees/${editing.id}`, form).catch(async (err) => {
            if (err.response?.status === 404 || err.response?.status === 405) {
              return await api.patch(`/admin/employees/${editing.id}`, form);
            }
            throw err;
          })
        : await api.post("/admin/employees", form);
      if (res.data?.temp_password) toast.success(`Employee created. Temporary password: ${res.data.temp_password}`, { duration: 15000 });
      else toast.success("Employee saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Deactivate this employee? Their leads will be unassigned.")) return;
    try {
      await api.delete(`/admin/employees/${id}`);
      toast.success("Employee removed");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="admin-employees-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-brand-dark">Employees & Roles</h1>
          <p className="text-sm text-brand-muted mt-0.5">Team members who can access this CRM</p>
        </div>
        {canManage && <button onClick={() => open(null)} data-testid="add-employee-btn" className="btn-primary !px-5 !py-2.5 !text-xs"><Plus size={14} /> Add Employee</button>}
      </div>

      {editing && (
        <form onSubmit={save} className="bg-white rounded-xl border border-black/5 p-5 mb-5 grid sm:grid-cols-3 gap-3" data-testid="employee-form">
          <input required className="input-site" placeholder="Full Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-name" />
          <input required type="email" className="input-site" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email" />
          <input className="input-site" placeholder="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
          <select className="input-site" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="emp-role">
            {Object.entries(ROLES)
              .filter(([k]) => user.role === "super_admin" || RANK[k] < RANK[user.role])
              .map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input className="input-site" placeholder="Department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
          <select className="input-site" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="active">Active</option><option value="inactive">Inactive</option>
          </select>
          <input type="date" className="input-site" value={form.joining_date || ""} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} aria-label="Joining date" />
          <input className="input-site" placeholder={editing?.id ? "New password (leave blank to keep)" : "Password (blank = auto-generate)"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="emp-password" />
          <div className="flex gap-2 items-end">
            <button type="submit" data-testid="emp-save-btn" className="btn-primary !px-5 !py-2.5 !text-xs">Save</button>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline !px-4 !py-2.5 !text-xs"><X size={13} /></button>
          </div>
        </form>
      )}

      {!rows ? <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-brand-red" size={28} /></div> : (
        <div className="bg-white rounded-xl border border-black/5 overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-brand-muted border-b border-black/5">
                <th className="px-5 py-3 font-bold">Name</th><th className="px-5 py-3 font-bold">Email</th>
                <th className="px-5 py-3 font-bold">Role</th><th className="px-5 py-3 font-bold">Department</th>
                <th className="px-5 py-3 font-bold">Status</th>{canManage && <th className="px-5 py-3 font-bold">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className="border-b border-black/5 last:border-0">
                  <td className="px-5 py-3 font-semibold text-brand-dark">{e.name}</td>
                  <td className="px-5 py-3 text-brand-dark/80 text-xs">{e.email}</td>
                  <td className="px-5 py-3"><span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-brand-cream border border-black/10">{ROLES[e.role] || e.role}</span></td>
                  <td className="px-5 py-3 text-xs text-brand-muted">{e.department || "—"}</td>
                  <td className="px-5 py-3"><span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${e.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}>{e.status}</span></td>
                  {canManage && (
                    <td className="px-5 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => open(e)} data-testid={`emp-edit-${e.id}`} className="text-brand-muted hover:text-brand-dark"><Pencil size={15} /></button>
                        {user.role === "super_admin" && e.id !== user.id && (
                          <button onClick={() => remove(e.id)} data-testid={`emp-delete-${e.id}`} className="text-brand-muted hover:text-brand-red"><Trash2 size={15} /></button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
